import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Typography, Statistic, Form, Input, Button, message, Timeline, Tag, Divider, Space, Avatar, Descriptions, Spin } from 'antd';
import { UserOutlined, MailOutlined, LockOutlined, CalendarOutlined, BarChartOutlined, FileTextOutlined, SafetyCertificateOutlined, HistoryOutlined, KeyOutlined } from '@ant-design/icons';
import { User, Mail, Shield, Calendar as CalendarIcon, Key, BarChart, FileText, CheckCircle } from 'lucide-react';
import dayjs from 'dayjs';
import { fetchUserMetrics, changePassword } from '../utils/api';
import './Profile.css';

const { Title, Text } = Typography;

const Profile = ({ user: propUser }) => {
    const [user, setUser] = useState(propUser);
    const [metrics, setMetrics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [form] = Form.useForm();

    useEffect(() => {
        if (!user && !propUser) {
            const storedUser = localStorage.getItem('spatium_user');
            if (storedUser) {
                setUser(JSON.parse(storedUser));
            }
        } else if (propUser) {
            setUser(propUser);
        }
        loadMetrics();
    }, [propUser]);

    const loadMetrics = async () => {
        try {
            setLoading(true);
            const data = await fetchUserMetrics();
            setMetrics(data);
        } catch (error) {
            console.error('Error fetching metrics:', error);
            message.error('Failed to load user metrics');
        } finally {
            setLoading(false);
        }
    };

    const onPasswordChange = async (values) => {
        try {
            setPasswordLoading(true);
            await changePassword(values.currentPassword, values.newPassword);
            message.success('Password changed successfully!');
            form.resetFields();
        } catch (error) {
            message.error(error.message || 'Failed to change password');
        } finally {
            setPasswordLoading(false);
        }
    };

    if (!user) return <div style={{ padding: '50px', textAlign: 'center' }}><Spin size="large" /></div>;

    return (
        <div className="profile-container">
            <div className="profile-header">
                <Space size={24} align="center">
                    <Avatar size={80} icon={<UserOutlined />} className="profile-avatar" />
                    <div>
                        <Title level={2} style={{ margin: 0 }}>{user.name}</Title>
                        <Text type="secondary"><MailOutlined /> {user.email}</Text>
                        <div style={{ marginTop: 8 }}>
                            <Tag color={user.role === 'admin' ? 'red' : 'blue'}>
                                <Shield size={12} style={{ marginRight: 4 }} />
                                {user.role?.toUpperCase()}
                            </Tag>
                            <Tag color="green">
                                <CheckCircle size={12} style={{ marginRight: 4 }} />
                                Active Account
                            </Tag>
                        </div>
                    </div>
                </Space>
            </div>

            <Row gutter={[24, 24]}>
                <Col xs={24} md={16}>
                    <Card title={<span><BarChartOutlined /> Key Metrics</span>} className="metrics-card h-full">
                        {loading ? <Spin /> : (
                            <Row gutter={[16, 16]}>
                                <Col span={8}>
                                    <Statistic
                                        title="Invoices Created"
                                        value={metrics?.invoicesCreated || 0}
                                        prefix={<FileTextOutlined />}
                                    />
                                </Col>
                                <Col span={8}>
                                    <Statistic
                                        title="Quotations Created"
                                        value={metrics?.quotationsCreated || 0}
                                        prefix={<FileText size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} />}
                                    />
                                </Col>
                                <Col span={8}>
                                    <Statistic
                                        title="Total Value"
                                        value={metrics?.totalValueGenerated || 0}
                                        precision={2}
                                        prefix="₹"
                                    />
                                </Col>
                                <Divider style={{ margin: '12px 0' }} />
                                <Col span={24}>
                                    <Title level={5}>Invoice Summary by Type</Title>
                                    <Row gutter={[16, 16]}>
                                        {(metrics?.summaryByType || []).map((type, idx) => (
                                            <Col key={idx} xs={12} sm={8}>
                                                <Card size="small" className="summary-type-card">
                                                    <Statistic
                                                        title={type._id?.toUpperCase()}
                                                        value={type.count}
                                                        suffix={`(${dayjs().format('YYYY')})`}
                                                    />
                                                    <Text type="secondary">Value: ₹{type.totalValue?.toLocaleString()}</Text>
                                                </Card>
                                            </Col>
                                        ))}
                                        {(!metrics?.summaryByType || metrics.summaryByType.length === 0) && (
                                            <Col span={24}><Text type="secondary">No invoices created yet.</Text></Col>
                                        )}
                                    </Row>
                                </Col>
                            </Row>
                        )}
                    </Card>
                </Col>

                <Col xs={24} md={8}>
                    <Card title={<span><KeyOutlined /> Change Password</span>} className="password-card">
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={onPasswordChange}
                        >
                            <Form.Item
                                name="currentPassword"
                                label="Current Password"
                                rules={[{ required: true, message: 'Current password is required' }]}
                            >
                                <Input.Password prefix={<LockOutlined />} placeholder="Enter current password" />
                            </Form.Item>
                            <Form.Item
                                name="newPassword"
                                label="New Password"
                                rules={[
                                    { required: true, message: 'New password is required' },
                                    { min: 6, message: 'Password must be at least 6 characters' }
                                ]}
                            >
                                <Input.Password prefix={<LockOutlined />} placeholder="Enter new password" />
                            </Form.Item>
                            <Form.Item
                                name="confirmPassword"
                                label="Confirm New Password"
                                dependencies={['newPassword']}
                                rules={[
                                    { required: true, message: 'Please confirm your password' },
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            if (!value || getFieldValue('newPassword') === value) {
                                                return Promise.resolve();
                                            }
                                            return Promise.reject(new Error('Passwords do not match'));
                                        },
                                    }),
                                ]}
                            >
                                <Input.Password prefix={<LockOutlined />} placeholder="Confirm new password" />
                            </Form.Item>
                            <Form.Item>
                                <Button type="primary" htmlType="submit" loading={passwordLoading} block>
                                    Update Password
                                </Button>
                            </Form.Item>
                        </Form>
                    </Card>
                </Col>

                <Col xs={24} md={12}>
                    <Card title={<span><HistoryOutlined /> Module Access</span>} className="access-card">
                        <Descriptions column={1} bordered size="small">
                            {Object.entries(user.moduleAccess || {}).map(([key, val]) => (
                                <Descriptions.Item key={key} label={key.charAt(0).toUpperCase() + key.slice(1)}>
                                    {val ? <Tag color="success">Granted</Tag> : <Tag color="error">Restricted</Tag>}
                                </Descriptions.Item>
                            ))}
                        </Descriptions>
                    </Card>
                </Col>

                <Col xs={24} md={12}>
                    <Card title={<span><CalendarOutlined /> Account Information</span>} className="info-card">
                        <Descriptions column={1} size="middle">
                            <Descriptions.Item label="Member Since">
                                {dayjs(user.createdAt).format('DD MMMM YYYY')}
                            </Descriptions.Item>
                            <Descriptions.Item label="Last Login">
                                {user.lastLogin ? dayjs(user.lastLogin).format('DD MMMM YYYY, h:mm A') : 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Default Theme">
                                <Tag color="blue">Light Mode</Tag>
                            </Descriptions.Item>
                        </Descriptions>
                        <Divider />
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            Account ID: {user.id}
                        </Text>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default Profile;
